import { useMemo } from 'react'
import moment from 'moment'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import Pane from 'components/Common/Pane'
import BodyText from 'LuxKit/Typography/BodyText'
import LuxuryEscapesLogo from 'components/App/Header/Logo/logos/LuxuryEscapesLogo'
import { QRCodeComponent } from 'components/Common/QRCode'
import { getSimpleImageUrl } from 'lib/image/imageUtils'

const LOUNGEKEY_LOGO_ID = 'inf5qbdgwmkc8968fqfz'

const COLLINSON_PURPLE = '#683E86'

/**
 * Extracts the short barcode reference from a Collinson LSP string.
 * LSP format: productCode/creationDate/startDateTime/endDateTime/reference/validFor/validIn/name/reserved/checksum
 * The reference (index 4) is the barcode number shown on the voucher.
 */
function extractBarcodeReference(voucherCode: string): string {
  const parts = voucherCode.split('/')
  if (parts.length >= 5 && parts[0] === 'LS') {
    return parts[4]
  }
  return voucherCode
}

const VoucherCard = styled(Pane)`
  width: 100%;
  max-width: ${rem(600)};
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 ${rem(2)} ${rem(12)} rgba(0, 0, 0, 0.1);
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  @media (max-width: 620px) {
    max-width: 100%;
  }
  @media print {
    max-width: ${rem(600)};
    box-shadow: none;
    border: none;
  }
`

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${rem(28)} ${rem(24)} 0;
  gap: ${rem(16)};
  @media (max-width: 480px) {
    padding: ${rem(20)} ${rem(16)} 0;
    gap: ${rem(12)};
  }
`

const LoungeKeyLogo = styled.img`
  width: ${rem(120)};
  max-width: ${rem(120)};
  height: auto;
  object-fit: contain;
  @media (max-width: 480px) {
    width: ${rem(100)};
    max-width: ${rem(100)};
  }
  @media print {
    width: ${rem(120)} !important;
    max-width: ${rem(120)} !important;
  }
`

const QRCodeWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 2;
  margin-bottom: ${rem(-100)};
  background: white;
  padding: ${rem(14)};
  border-radius: 0;
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
  svg {
    width: 100%;
    max-width: ${rem(140)};
    height: auto;
  }
  @media (max-width: 480px) {
    margin-bottom: ${rem(-80)};
    svg {
      max-width: ${rem(120)};
    }
  }
  @media print {
    background: white !important;
    padding: ${rem(14)};
    svg {
      max-width: ${rem(140)};
    }
  }
`

const DIAGONAL_OFFSET = 70
const DIAGONAL_OFFSET_MOBILE = 50

const PurpleSection = styled.div`
  width: 100%;
  background: ${COLLINSON_PURPLE};
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  clip-path: polygon(
    0 0,
    100% ${rem(DIAGONAL_OFFSET)},
    100% 100%,
    0 calc(100% - ${rem(DIAGONAL_OFFSET)})
  );
  padding: ${rem(115)} ${rem(24)} ${rem(85)};
  gap: ${rem(14)};
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
  @media print {
    background: ${COLLINSON_PURPLE} !important;
    color: white !important;
    * {
      color: white !important;
    }
  }
  @media (max-width: 480px) {
    clip-path: polygon(
      0 0,
      100% ${rem(DIAGONAL_OFFSET_MOBILE)},
      100% 100%,
      0 calc(100% - ${rem(DIAGONAL_OFFSET_MOBILE)})
    );
    padding: ${rem(90)} ${rem(16)} ${rem(65)};
    gap: ${rem(12)};
  }
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(2)};
`

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${rem(20)} ${rem(24)};
  gap: ${rem(16)};
  @media (max-width: 480px) {
    padding: ${rem(16)} ${rem(16)};
    gap: ${rem(14)};
  }
`

const ConditionsSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(6)};
`

// BodyText colour prop only accepts design tokens — Collinson purple is a brand-specific color not in the palette
const ConditionsTitle = styled(BodyText)`
  color: ${COLLINSON_PURPLE};
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
  @media print {
    color: ${COLLINSON_PURPLE} !important;
  }
`

const ConditionsList = styled.ul`
  padding-left: ${rem(16)};
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  align-self: flex-start;
`

const FooterSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
  padding-top: ${rem(14)};
`

const FooterLogos = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${rem(48)};
  @media (max-width: 360px) {
    gap: ${rem(32)};
  }
`

const FooterLoungeKeyLogo = styled.img`
  width: ${rem(90)};
  height: auto;
  object-fit: contain;
  @media (max-width: 360px) {
    width: ${rem(70)};
  }
  @media print {
    width: ${rem(90)};
  }
`

const StyledLuxuryEscapesLogo = styled(LuxuryEscapesLogo)`
  width: ${rem(160)};
  height: auto;
  @media (max-width: 360px) {
    width: ${rem(120)};
  }
  @media print {
    width: ${rem(160)};
  }
`

interface Props {
  address: string
  voucher: App.ExperienceVoucher
  experienceOffer?: App.ExperienceOffer
}

export function PrintCollinsonLoungeVoucher({ voucher }: Props) {
  const intl = useIntl()
  const loungeKeyLogoAlt = intl.formatMessage({
    id: 'HLwPTl5yR1utymLTKBh4',
    defaultMessage: 'LoungeKey',
    description: 'Alt text for the LoungeKey logo shown on the printable Collinson lounge voucher',
  })

  const nameLabel = intl.formatMessage({
    id: 'a7eQPzqwmnsE7eQp04_i',
    defaultMessage: 'Name',
    description: 'Label for customer name on Collinson lounge voucher',
  })

  const barcodeReferenceLabel = intl.formatMessage({
    id: 'VPefmdQzLohABHc6DVX8',
    defaultMessage: 'Barcode Reference Number',
    description: 'Label for barcode reference number on Collinson lounge voucher',
  })

  const startDateLabel = intl.formatMessage({
    id: 'oLTPeKMYy6xRJy0YAE4p',
    defaultMessage: 'Start Date',
    description: 'Field label for the validity start date on the printable Collinson lounge voucher',
  })

  const expiryDateLabel = intl.formatMessage({
    id: 'O9jb9aTdasssmy0cYFST',
    defaultMessage: 'Expiry Date',
    description: 'Field label for the validity expiry date on the printable Collinson lounge voucher',
  })

  const summaryOfConditionsLabel = intl.formatMessage({
    id: 'c5QR2P7rz0xXxEgrqdAX',
    defaultMessage: 'Summary of conditions',
    description: 'Title for conditions section on Collinson lounge voucher',
  })

  const providedByLabel = intl.formatMessage({
    id: '3koakC97CEjO3XF39yo5',
    defaultMessage: 'Provided by',
    description: 'Text above partner logos on Collinson lounge voucher footer',
  })

  const notAvailableLabel = intl.formatMessage({
    id: 'DpgZmQJ61dp5N0045IiV',
    defaultMessage: 'N/A',
    description: 'Fallback text when date is not available on Collinson lounge voucher',
  })

  const condition1 = intl.formatMessage({
    id: '0Fq4JK88uwyVdRVJZF_w',
    defaultMessage: 'This LoungeKey code does not permit additional guests into the lounge.',
    description: 'Collinson lounge voucher condition about guest restrictions',
  })

  const condition2 = intl.formatMessage({
    id: 'eQ74CHvXfNFvjeQKba0P',
    defaultMessage: 'The LoungeKey code may be accepted on a mobile device (i.e. smartphone or tablet) as long as it is clear.',
    description: 'Collinson lounge voucher condition about mobile device acceptance',
  })

  const condition3 = intl.formatMessage({
    id: 'cfNfLToV7S-Cqg8Lk5_J',
    defaultMessage: 'If the barcode is not clear, manually type in the reference number above.',
    description: 'Collinson lounge voucher condition about manual barcode entry',
  })

  const conditions = useMemo(() => [condition1, condition2, condition3], [condition1, condition2, condition3])

  const customerName = useMemo(() => {
    return [voucher.customer?.name, voucher.customer?.surname].filter(Boolean).join(' ')
  }, [voucher.customer])

  const barcodeReference = useMemo(() => {
    return extractBarcodeReference(voucher.providerItemId || voucher.voucherCode)
  }, [voucher.voucherCode, voucher.providerItemId])

  const startDate = useMemo(() => {
    if (!voucher.bookingStartDate) return notAvailableLabel
    return moment.utc(voucher.bookingStartDate).format('DD-MMM-YYYY')
  }, [voucher.bookingStartDate, notAvailableLabel])

  const expiryDate = useMemo(() => {
    if (!voucher.bookingStartDate) return notAvailableLabel
    return moment.utc(voucher.bookingStartDate).add(12, 'months').subtract(1, 'day').format('DD-MMM-YYYY')
  }, [voucher.bookingStartDate, notAvailableLabel])

  const qrCodeValue = useMemo(() => {
    return voucher.qrCodeUrl || voucher.voucherCode
  }, [voucher.qrCodeUrl, voucher.voucherCode])

  return (
    <VoucherCard border="light-foreground" borderRadius="M">
      <TopSection>
        <LoungeKeyLogo src={getSimpleImageUrl(LOUNGEKEY_LOGO_ID)} alt={loungeKeyLogoAlt} />
        <QRCodeWrapper>
          <QRCodeComponent value={qrCodeValue} size={140} />
        </QRCodeWrapper>
      </TopSection>

      <PurpleSection>
        <FieldGroup>
          <BodyText variant="medium" weight="bold" align="center">{nameLabel}</BodyText>
          <BodyText variant="medium" weight="bold" align="center">{customerName}</BodyText>
        </FieldGroup>

        <FieldGroup>
          <BodyText variant="medium" weight="bold" align="center">{barcodeReferenceLabel}</BodyText>
          <BodyText variant="medium" weight="bold" align="center">{barcodeReference}</BodyText>
        </FieldGroup>

        <FieldGroup>
          <BodyText variant="medium" weight="bold" align="center">{startDateLabel}</BodyText>
          <BodyText variant="medium" weight="bold" align="center">{startDate}</BodyText>
        </FieldGroup>

        <FieldGroup>
          <BodyText variant="medium" weight="bold" align="center">{expiryDateLabel}</BodyText>
          <BodyText variant="medium" weight="bold" align="center">{expiryDate}</BodyText>
        </FieldGroup>
      </PurpleSection>

      <BottomSection>
        <ConditionsSection>
          <ConditionsTitle variant="large" weight="bold" align="center">{summaryOfConditionsLabel}</ConditionsTitle>
          <ConditionsList>
            {conditions.map((condition, index) => (
              <li key={index}><BodyText variant="small" colour="light-foreground-alt">{condition}</BodyText></li>
            ))}
          </ConditionsList>
        </ConditionsSection>

        <FooterSection>
          <BodyText variant="small">{providedByLabel}</BodyText>
          <FooterLogos>
            <FooterLoungeKeyLogo src={getSimpleImageUrl(LOUNGEKEY_LOGO_ID)} alt={loungeKeyLogoAlt} />
            <StyledLuxuryEscapesLogo />
          </FooterLogos>
        </FooterSection>
      </BottomSection>
    </VoucherCard>
  )
}
